import { useState, useEffect, useRef } from 'react';
import { useGetDriverCustomerConfigsQuery } from '../queries/getDriverCustomerConfigs.query.generated';
import { useUser } from '@store';
import { enrichCustomerConfig } from '@utils/configsService';
import {
  getEnabledProductsFromConfigs,
  type MobilityAttribute,
} from '@utils/productMapper';

/**
 * Resolves the set of mobility attributes available to a driver by
 * aggregating `allowed_move_types` across all of the driver's linked
 * customer configs.
 *
 * Flow:
 *   1. Query usertocustomers → customer.config + org.config
 *   2. Enrich each customer config via the existing Netlify function
 *   3. Union all allowed_move_types
 *   4. Map to mobility attributes via productMapper
 *
 * In practice drivers are linked to 4-5 customers, so this is lightweight.
 */
export function useDriverProducts(userId: number | undefined) {
  const user = useUser();
  const [enabledProducts, setEnabledProducts] = useState<Set<MobilityAttribute>>(new Set());
  const [loading, setLoading] = useState(true);
  // Track which userId we've already enriched to avoid re-running
  const enrichedForRef = useRef<string | null>(null);

  const { data, loading: queryLoading } = useGetDriverCustomerConfigsQuery({
    variables: { userId: userId! },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (queryLoading || !data?.usertocustomers) return;

    const customers = data.usertocustomers;
    // Build a stable key so we don't re-enrich when nothing changed
    const cacheKey = customers.map(c => c.customer_id).sort().join(',');
    if (enrichedForRef.current === cacheKey) return;

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      try {
        const token = await user?.getToken();
        if (!token || cancelled) return;

        // Enrich each customer config in parallel
        const enrichedConfigs = await Promise.all(
          customers.map(utc =>
            enrichCustomerConfig(
              token,
              utc.customer.config,
              utc.customer.organization?.config,
            ).catch(() => null), // Swallow individual failures
          ),
        );

        if (cancelled) return;

        // Move-type products (lite rooftops only) + service-flag products
        // (e.g. Mobile Service) across every linked customer
        setEnabledProducts(getEnabledProductsFromConfigs(
          enrichedConfigs.map((cfg, i) => ({
            // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
            tier: customers[i].customer.tier,
            enrichedConfig: cfg,
          })),
        ));
        enrichedForRef.current = cacheKey;
      } catch (err) {
        console.error('Error resolving driver products:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [data, queryLoading, user]);

  return { enabledProducts, loading: loading && queryLoading };
}
